import { API_URL } from './api-config.js';

/**
 * Fetches case summary using the optimized endpoint and returns counts
 * in the shape expected by the UI.
 *
 * Expected API response shape:
 * {
 *   "total_cases": 123,
 *   "by_status": {"OPEN": 10, "APPROVED": 20, ...},
 *   "total_counts": {
 *     "total_cases": 456,
 *     "by_status": {"OPEN": 50, "APPROVED": 100, ...}
 *   }
 * }
 * 
 * Where:
 * - total_cases and by_status are filtered by date (for case summary)
 * - total_counts contains unfiltered counts (for other sample prompts)
 */
export const fetchCaseSummary = async () => {
  const defaultCounts = {
    open: 0,
    closed: 0,
    approved: 0,
    rejected: 0,
    onHold: 0,
    compliant: 0,
    nonCompliant: 0,
    exception: 0,
    total: 0,
    closedAfrCases: 0,
  };

  try {
    // Compute last 6 days window using explicit dates (YYYY-MM-DD)
    const end = new Date();
    const start = new Date(end);
    // Subtract 6 days
    start.setDate(start.getDate() - 6);
    const toIsoDate = (d) => d.toISOString().slice(0, 10);
    const startDate = toIsoDate(start);
    const endDate = toIsoDate(end);

    // Resolve the logged-in user's email to scope results server-side
    let userEmail = null;
    try {
      const rawUserInfo = sessionStorage.getItem('userInfo');
      if (rawUserInfo) {
        const parsed = JSON.parse(rawUserInfo);
        userEmail = parsed?.email || parsed?.userEmail || null;
      }
    } catch (_) {
      // ignore JSON parse errors
    }

    const emailParam = userEmail ? `&user_email=${encodeURIComponent(userEmail)}` : '';
    const url = `${API_URL}/case_status_summary?start_date=${startDate}&end_date=${endDate}${emailParam}`;
    
    const response = await fetch(url);
    const data = await response.json();
    

    if (!data || typeof data !== 'object') return defaultCounts;

    // Use date-filtered total for case summary display
    const total = Number(data.total_cases) || 0;
    
    // Use total_counts (without date filters) for individual status prompts
    const totalCounts = data.total_counts || {};
    const byStatusTotal = totalCounts.by_status && typeof totalCounts.by_status === 'object' ? totalCounts.by_status : {};

    // Normalize keys to uppercase and numeric values for total counts
    const normalized = Object.keys(byStatusTotal).reduce((acc, k) => {
      const key = String(k).trim().toUpperCase();
      const val = Number(byStatusTotal[k]) || 0;
      acc[key] = (acc[key] || 0) + val;
      return acc;
    }, {});

    const sumFor = (pred) =>
      Object.entries(normalized).reduce((s, [k, v]) => (pred(k) ? s + v : s), 0);

    const counts = {
      // Use total counts (without date filter) for individual status prompts
      open: normalized['OPEN'] || 0,
      closed: normalized['CLOSED'] || 0,
      approved: normalized['APPROVED'] || 0,
      rejected: normalized['REJECTED'] || 0,
      onHold: sumFor(k => k.includes('ON') && k.includes('HOLD')),
      compliant: sumFor(k => k.includes('COMPLIANT') && !k.includes('NON')),
      nonCompliant: sumFor(k => k.includes('NON') && k.includes('COMPLIANT')),
      exception: normalized['EXCEPTION'] || 0,
      // Use date-filtered total for case summary prompt
      total,
      closedAfrCases: normalized['CLOSED'] || 0,
    };

    

    if (!normalized['CLOSED']) {
      try {
        const afrResponse = await fetch(`${API_URL}/getapprovedfreightcases`);
        const afrData = await afrResponse.json();
        counts.closedAfrCases = afrData.body?.data?.length || 0;
      } catch (error) {
        console.error("Error fetching closed AFR cases count:", error);
      }
    }

    
    return counts;
  } catch (error) {
    console.error('Error fetching case summary:', error);
    console.error('[fetchCaseSummary] Error details:', {
      message: error.message,
      stack: error.stack
    });
    return defaultCounts;
  }
};

/**
 * Fetches the count of total emails received today from the inbound_cases endpoint
 * @param {string} casestatus - Optional filter by case status
 * @param {string} country_name - Optional filter by country name
 * @returns {Promise<number>} Count of emails received today
 */
export const fetchTodayEmailCount = async (casestatus = null, country_name = null) => {
  try {
    let url = `${API_URL}/inbound_cases/today/count`;
    const params = new URLSearchParams();
    
    if (casestatus) {
      params.append('casestatus', casestatus);
    }
    if (country_name) {
      params.append('country_name', country_name);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch email count: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    return data.count || 0;
  } catch (error) {
    console.error('Error fetching today email count:', error);
    return 0;
  }
};
